import { debounce } from 'debounce';
import { useState, useCallback, Dispatch, SetStateAction, useMemo } from 'react';
import { ITreeNode } from './TreeNode';

export const UPDATE_EVENT_PREFIX = `update-tree__`;

export enum TreeCommandType {
  Add,
  Delete,
  Select,
  Refresh,
  Load,
}

export interface ITreeCommand {
  command?: TreeCommandType;
  id?: string;
}

export type TreeNodeMouseEventHandler<T> = (
  node: ITreeNode<T>,
  e: React.MouseEvent<HTMLElement>
) => Promise<void> | void;

/**
 * @deprecated Please use /CommonTree/useTreeStore.ts
 */
interface IUseTreeModel<T> {
  model: ITreeNode<T>[];
  /**
   * Updates existing node
   */
  setNode: (node: ITreeNode<T>) => void;
  setNodes: (node: ITreeNode<T>[]) => void;
  removeNode: (nodeId: string) => void;
  expandNode: (nodeId: string, isExpanded: boolean, element?: HTMLElement) => Promise<void>;
  toggleExpandNode: TreeNodeMouseEventHandler<T>;
  addNodeChildren: (nodeId: string, children: ITreeNode<T>[]) => void;
  toggleSelectNode: (nodeId: string, selected: boolean) => void;
  setModel: Dispatch<SetStateAction<ITreeNode<T>[]>>;
  toggleSelectNodes: (nodeIds: string[], selected: boolean) => void;
  getActiveNodes: () => ITreeNode<T>[];
  unselectNodes: () => void;
  findNode: (id: string) => ITreeNode<T> | null;
  findNodes: (filter: (node: ITreeNode<T>) => boolean) => ITreeNode<T>[];
}

/**
 * @deprecated Please use /CommonTree/useTreeStore.ts
 */
export function useTreeModel<T>(
  nodes: ITreeNode<T>[],
  loadNodes?: (parent: ITreeNode<T>) => Promise<ITreeNode<T>[]>,
  onNodeExpanded?: (isExpanded: boolean, element?: HTMLElement) => void
): IUseTreeModel<T> {
  const [model, setModel] = useState<ITreeNode<T>[]>(nodes);
  const getActiveNodes = useCallback(() => {
    return findNodesList(node => !!node.isActive, model);
  }, [model]);

  const setNode = useCallback(
    (node: ITreeNode<T>) => {
      let updatableNode = getNode(node.nodeId, model);

      if (updatableNode) {
        Object.assign(updatableNode, node);

        setModel([...model]);
      }
    },
    [model]
  );

  const findNode = useCallback(
    (id: string) => {
      return getNode(id, model);
    },
    [model]
  );

  const findNodes = useCallback(
    (filter: (node: ITreeNode<T>) => boolean) => {
      return findNodesList(filter, model);
    },
    [model]
  );

  const setNodes = useCallback(
    (nodes: ITreeNode<T>[]) => {
      nodes.forEach(node => {
        let updatableNode = getNode(node.nodeId, model);

        if (updatableNode) {
          Object.assign(updatableNode, node);
        }
      });
      setModel([...model]);
    },
    [model]
  );

  const removeNode = useCallback(
    (nodeId: string) => {
      if (deleteNode(nodeId, model)) {
        setModel([...model]);
      }
    },
    [model]
  );
  const afterNodeExpanded = useMemo(() => debounce(onNodeExpanded ? onNodeExpanded : () => {}, 200), [onNodeExpanded]);

  const expandNode = useCallback(
    async (nodeId: string, isExpanded: boolean, element?: HTMLElement) => {
      let updatableNode = getNode(nodeId, model);

      if (updatableNode) {
        updatableNode.isExpanded = isExpanded;
        const shouldLoad =
          isExpanded && loadNodes && (updatableNode.alwaysLoadChildren || !updatableNode.children?.length);

        if (shouldLoad && loadNodes) {
          updatableNode.isLoading = true;
          // toDo improve animation logic
          loadNodes(updatableNode).then(result => {
            updatableNode && (updatableNode.children = result);
            updatableNode && (updatableNode.isLoading = false);
            setModel([...model]);
            afterNodeExpanded(isExpanded, element);
          });
        }

        setModel([...model]);
        if (!shouldLoad) {
          afterNodeExpanded(isExpanded, element);
        }
      }
    },
    [afterNodeExpanded, loadNodes, model]
  );

  const toggleExpandNode = useCallback(
    async (node: ITreeNode<T>, e: React.MouseEvent<HTMLElement>) => {
      await expandNode(node.nodeId, !node.isExpanded, e.currentTarget);
    },
    [expandNode]
  );

  const toggleSelectNode = useCallback(
    async (nodeId: string, selected: boolean) => {
      let updatableNode = getNode(nodeId, model);

      if (updatableNode) {
        updatableNode.isActive = selected;

        setModel([...model]);
      }
    },
    [model]
  );

  const toggleSelectNodes = useCallback(
    async (nodeIds: string[], selected: boolean) => {
      let updatableNodes = findNodesList(node => nodeIds.indexOf(node.nodeId) !== -1, model);

      if (updatableNodes.length) {
        updatableNodes.forEach(node => (node.isActive = selected));

        setModel([...model]);
      }
    },
    [model]
  );

  const unselectNodes = useCallback(() => {
    getActiveNodes().forEach(node => toggleSelectNode(node.nodeId, false));
  }, [getActiveNodes, toggleSelectNode]);

  const addNodeChildren = useCallback(
    (nodeId: string, children: ITreeNode<T>[]) => {
      let updatableNode = getNode(nodeId, model);

      if (updatableNode) {
        updatableNode.children ? updatableNode.children.push(...children) : (updatableNode.children = children);
        setModel([...model]);
      }
    },
    [model]
  );

  return {
    model,
    setNode,
    removeNode,
    expandNode,
    addNodeChildren,
    setModel,
    toggleSelectNode,
    getActiveNodes,
    unselectNodes,
    toggleSelectNodes,
    setNodes,
    findNodes,
    findNode,
    toggleExpandNode,
  };
}

function getNode<T>(id: string, nodes: ITreeNode<T>[]): ITreeNode<T> | null {
  let result: ITreeNode<T> | null = null;

  for (const node of nodes) {
    if (node.nodeId === id) {
      result = node;
    } else if (node.children?.length) {
      result = getNode(id, node.children);
    }
    if (result) {
      break;
    }
  }

  return result;
}

export function findNodesList<T>(filter: (node: ITreeNode<T>) => boolean, nodes: ITreeNode<T>[]): ITreeNode<T>[] {
  let result: ITreeNode<T>[] = [];

  for (const node of nodes) {
    if (filter(node)) {
      result.push(node);
    }
    if (node.children?.length) {
      result.push(...findNodesList(filter, node.children));
    }
  }

  return result;
}

export function deleteNode<T>(id: string, nodes: ITreeNode<T>[]): boolean {
  let result = false;
  let i = 0;

  for (const node of nodes) {
    if (node.nodeId === id) {
      nodes.splice(i, 1);
      result = true;
    }
    if (node.children?.length) {
      if (deleteNode(id, node.children)) {
        result = true;
      }
    }
    i++;
  }

  return result;
}

export function scrollToElement(element: Element | undefined) {
  const parent = element?.closest('li');
  const scrollHolder = document.getElementById('collapseFolders');
  const contHeight = scrollHolder?.offsetHeight || 0;
  const elemHeight = parent?.offsetHeight || 0;

  if (elemHeight > contHeight) {
    parent?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  } else {
    const elemClientRect = parent?.getBoundingClientRect();
    const scrollHolderClientRect = scrollHolder?.getBoundingClientRect();

    if (
      (elemClientRect?.bottom || 0) > (scrollHolderClientRect?.bottom || 0) ||
      (elemClientRect?.top || 0) < (scrollHolderClientRect?.top || 0)
    ) {
      parent?.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }
  }
}
